/**
 * Automatic session start after GPS arrival (owner decision 2026-10-07:
 * "automatic with grace + undo").
 *
 * THE DWELL RULE (documented contract):
 *
 *  1. The grace clock is anchored at the journey's `arrivedAt` (the GPS
 *     auto-arrival moment). The session auto-starts at
 *     `arrivedAt + AUTO_START_DWELL_MS` (3 minutes).
 *  2. While waiting, the device keeps watching GPS. A fix only counts when its
 *     reported accuracy is ≤ ARRIVAL_MAX_ACCURACY_METERS (same gate as
 *     auto-arrival — the 97-hour-bug lesson; coarse fixes are ignored
 *     entirely).
 *  3. A counted fix OUTSIDE ARRIVAL_GEOFENCE_RADIUS_METERS (the provider drove
 *     off / wrong house) RESETS the clock: the deadline moves to that fix's
 *     time + the full dwell. It keeps re-anchoring while they remain outside.
 *  4. NO fixes is NOT a contradiction: GPS routinely dies the moment the
 *     provider walks indoors, which is exactly the dwell's happy path — the
 *     timer proceeds. (Arrival itself was already proven by an accurate fix.)
 *  5. The timer runs only while the app is foregrounded on the session (the
 *     WebView gets no GPS or timers in the background). If the grace deadline
 *     passed while the app was away, auto-start does NOT fire retroactively on
 *     resume — the recorded start time must be the real end-of-grace moment,
 *     not "whenever the phone came back out". Manual start is the path then.
 *  6. The auto-start submits the normal check-in lifecycle event with
 *     `startedBy: 'auto-gps'` (server-validated, idempotent, audited on the
 *     session row). Undo gets a LONGER window than a manual start
 *     (UNDO_AUTO_START_WINDOW_MS — the provider may not be looking at the
 *     phone), and an undone auto-start NEVER re-arms for that visit: manual
 *     start only (see `canArmAutoStart`).
 */

import {
  ARRIVAL_GEOFENCE_RADIUS_METERS,
  ARRIVAL_MAX_ACCURACY_METERS,
} from './journey'
import { checkGeofence } from './geofence'
import { isFaceToFace } from './telehealth'
import { UNDO_CHECK_IN_WINDOW_MS } from './state-machine'
import type { GeoPoint, Session, SessionJourney } from './types'

/** Dwell grace between GPS arrival and the automatic session start. */
export const AUTO_START_DWELL_MS = 3 * 60_000

/** Undo window for an AUTO start (vs UNDO_CHECK_IN_WINDOW_MS for manual). */
export const UNDO_AUTO_START_WINDOW_MS = 60_000

/**
 * Who triggered the session start — audited on the row (`startedBy`). `call`
 * = a telehealth session call connected (see session-call.ts).
 */
export type SessionStartedBy = 'auto-gps' | 'provider' | 'call'

/**
 * Client undo window for a given start trigger: a manual tap gets the short
 * snackbar window; an auto start gets a full minute (the provider may not be
 * looking at the phone when it fires). The server adds
 * UNDO_CHECK_IN_SERVER_GRACE_MS on top (state-machine.ts).
 */
export function undoWindowMsFor(startedBy: SessionStartedBy | null | undefined): number {
  // A call start is automatic too, and must stay undoable until the server's
  // recording-consent window (30 s after accept) can end the call — the
  // client still shows only a short snackbar (SESSION_CALL_UNDO_MS).
  return startedBy === 'auto-gps' || startedBy === 'call'
    ? UNDO_AUTO_START_WINDOW_MS
    : UNDO_CHECK_IN_WINDOW_MS
}

/**
 * Whether the auto-start may be ARMED for this session right now (rule 5/6):
 * in-person, journey arrived at a known time, no start recorded yet, and the
 * provider has not undone a start this visit (manual-only after an undo).
 */
export function canArmAutoStart(
  session: Pick<Session, 'faceToFace' | 'lifecycleState' | 'checkInUndone' | 'cancellationRequested'>,
  journey: SessionJourney | null | undefined,
): boolean {
  if (!isFaceToFace(session)) return false
  if (journey?.status !== 'arrived' || journey.arrivedAt == null) return false
  if (session.checkInUndone != null) return false
  // A pending member cancellation means the provider's next step is reviewing
  // it — never auto-start a session the member asked to cancel.
  if (session.cancellationRequested != null) return false
  return (
    session.lifecycleState === 'scheduled' ||
    session.lifecycleState === 'reminded' ||
    session.lifecycleState === 'check-in-available'
  )
}

/**
 * Initial auto-start deadline for an armed dwell (rule 1 + 5): `arrivedAt +
 * dwell`, or `null` when that moment has already passed at arming time (the
 * app was away through the grace — no retroactive starts).
 */
export function resolveAutoStartDeadline(
  journey: Pick<SessionJourney, 'status' | 'arrivedAt'> | null | undefined,
  now: number = Date.now(),
): number | null {
  if (journey?.status !== 'arrived' || journey.arrivedAt == null) return null
  const deadline = journey.arrivedAt + AUTO_START_DWELL_MS
  return deadline > now ? deadline : null
}

export type DwellFixVerdict = 'inside' | 'outside' | 'ignored'

/**
 * Classify a GPS fix against the dwell rule (rules 2–3): `ignored` when the
 * accuracy gate fails, otherwise `inside`/`outside` the arrival geofence.
 */
export function classifyDwellFix(
  fix: GeoPoint & { accuracy?: number },
  destination: GeoPoint | null | undefined,
): DwellFixVerdict {
  if (!destination) return 'ignored'
  const accuracy = fix.accuracy
  if (accuracy == null || !Number.isFinite(accuracy) || accuracy < 0) return 'ignored'
  if (accuracy > ARRIVAL_MAX_ACCURACY_METERS) return 'ignored'
  return checkGeofence(fix, destination, ARRIVAL_GEOFENCE_RADIUS_METERS).withinRadius
    ? 'inside'
    : 'outside'
}

/**
 * Apply a classified fix to the current deadline (rule 3): an `outside` fix
 * re-anchors the deadline to `fixTime + dwell`; `inside`/`ignored` leave it.
 */
export function nextAutoStartDeadline(
  currentDeadline: number,
  verdict: DwellFixVerdict,
  fixTime: number,
): number {
  if (verdict !== 'outside') return currentDeadline
  return fixTime + AUTO_START_DWELL_MS
}
